import {
  Component,
  ComponentRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AutomataDevelopmentService,
  AutomationInterface,
  Context,
  OutputParameter,
  TaskDefinition,
  WorkflowStepInterface,
} from '@vendasta/automata';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { Observable, throwError } from 'rxjs';
import { catchError, first, map, shareReplay, take } from 'rxjs/operators';
import { WORKFLOW_ACTIONS } from './../action-definitions';
import { CustomOutputParametersComponent } from '../custom-output-parameters/custom-output-parameters.component';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { GalaxyWrapModule } from '@vendasta/galaxy/galaxy-wrap';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { TaskDefinitionsService } from '../../../data-services/task-definitions.service';
import { CommonActionPanelInterface } from '../../components/common/common-action-panel-component.directive';
import { DynamicComponentDirective } from '../../../common/dynamic-component.directive';
import { Branch } from '../branch-options';
import { AutomationViewService } from '../../../automation/automation-view.service';
import { ValidationService } from '../../services';
import {
  AutomataClosingSideDrawerComponent,
  AutomataSideDrawerContentDirective,
  AutomataSideDrawerFooterDirective,
  AutomataSideDrawerPreTitleActionsDirective,
} from '../../side-drawer/closing-side-drawer/automata-closing-side-drawer.component';
import { TaskErrorDisclaimerComponent } from '../../components/common/controls/task-error-disclaimer/task-error-disclaimer.component';
import { AutomationsRouterService } from '../../../shared/automations-router.service';
import { AutomataContextService, ContextConfigMap } from '@galaxy/automata/shared';
import { CustomOutputParamsService } from '../custom-output-parameters/custom-output-params.service';
import { AppRequirementsAlertComponent } from '../../../shared/app-requirements-alert/app-requirements-alert.component';
import { InputParameter } from '@vendasta/automata/lib/_internal';
import { ActivatedRoute } from '@angular/router';
import { MatExpansionModule } from '@angular/material/expansion';
import { StepNameService } from '../../services/step-name.service';

@Component({
  selector: 'automata-dynamic-action-panel-loader',
  templateUrl: './dynamic-action-panel-loader.component.html',
  styleUrls: ['dynamic-action-panel-loader.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    MatButtonModule,
    GalaxyAlertModule,
    MatProgressSpinnerModule,
    MatIconModule,
    GalaxyFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    TaskErrorDisclaimerComponent,
    GalaxyWrapModule,
    CustomOutputParametersComponent,
    DynamicComponentDirective,
    MatInputModule,
    MatDividerModule,
    AutomataClosingSideDrawerComponent,
    AutomataSideDrawerContentDirective,
    AutomataSideDrawerFooterDirective,
    AutomataSideDrawerPreTitleActionsDirective,
    AppRequirementsAlertComponent,
    MatExpansionModule,
  ],
})
export class DynamicActionPanelLoaderComponent implements OnInit, OnChanges {
  @Input() step: WorkflowStepInterface;
  @Input() title: string;
  @Input() automation: AutomationInterface;
  @Input() taskDefinitionId: string;
  @Input() precedingSteps: string[];
  @Input() branch: Branch;
  @Input() groupId: string;
  @Input() isGoal = false;
  isEditable$: Observable<boolean>;
  isAvailableOnSubscriptionLevel$: Observable<boolean>;
  hasOutputParameters$: Observable<boolean>;
  taskDefinition$: Observable<TaskDefinition>;

  confirmationModal = inject(OpenConfirmationModalService);
  stepIdentifierControl: FormControl<string> = new FormControl<string>('', [
    Validators.required,
    Validators.maxLength(50),
  ]);
  stepNotesControl: UntypedFormControl = new UntypedFormControl('');
  form: UntypedFormGroup;
  name = '';
  supportsCustomOutputParameters = false;
  upgradeTitle = 'AUTOMATIONS.EDITOR.DEFAULT_UPGRADE_SUBSCRIPTION_TITLE';
  upgradeDescription = 'AUTOMATIONS.EDITOR.DEFAULT_UPGRADE_SUBSCRIPTION_DESCRIPTION';
  context = Context;
  inputParameters$: Observable<InputParameter[]>;
  outputParameters$: Observable<OutputParameter[]>;
  showDebugInfo$ = this.route.queryParamMap.pipe(
    take(1),
    map((params) => params.get('debug')),
    map((res) => !!res),
  );

  @ViewChild('viewSelectorTemplate', { static: true }) viewSelectorTemplate: TemplateRef<any>;
  @ViewChild(DynamicComponentDirective, { static: true }) taskNodeHost: DynamicComponentDirective;
  @ViewChildren('customOutputParameters') customOutputParametersComponent: QueryList<CustomOutputParametersComponent>;

  componentRef: ComponentRef<CommonActionPanelInterface>;
  public automationContext = this.automationContextService.currentContext;
  contextConfig = ContextConfigMap[this.automationContext];

  constructor(
    private readonly automationService: AutomationViewService,
    private readonly stepNameService: StepNameService,
    private readonly taskDefinitionsService: TaskDefinitionsService,
    private router: AutomationsRouterService,
    private readonly analyticsService: ProductAnalyticsService,
    private readonly fb: UntypedFormBuilder,
    protected readonly snackbarService: SnackbarService,
    protected readonly translateService: TranslateService,
    protected readonly validationService: ValidationService,
    private readonly automationContextService: AutomataContextService,
    private readonly customOutputParamsService: CustomOutputParamsService,
    private readonly automataDevelopmentService: AutomataDevelopmentService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.taskDefinitionsService.setCurrentTaskDefinitionId(this.taskDefinitionId, false);
    this.form = this.fb.group({
      stepIdentifier: this.stepIdentifierControl,
      stepNotes: this.stepNotesControl,
    });
    const components = WORKFLOW_ACTIONS[this.taskDefinitionId];
    this.name = components.name;
    this.stepIdentifierControl.setValue(
      this.step?.name ||
        (this.isGoal
          ? this.translateService.instant('AUTOMATIONS.EDITOR.AUTOMATION_GOAL')
          : this.stepNameService.buildBestAttemptUniqueName(this.name, this.automation)),
    );
    this.stepNotesControl.setValue(this.step?.notes || '');
    this.supportsCustomOutputParameters = components.supportsCustomOutputParameters;
    this.isEditable$ = this.automationService.isEditable$(this.automation);
    this.isAvailableOnSubscriptionLevel$ = this.taskDefinitionsService
      .checkTaskDefinitionAvailableAtSubscriptionLevel(this.taskDefinitionId)
      .pipe(take(1), shareReplay(1));
    this.hasOutputParameters$ = this.taskDefinitionsService.doesTaskDefinitionHaveOutputParameters(
      this.taskDefinitionId,
    );
    this.taskDefinition$ = this.automataDevelopmentService.getTaskDefinition(this.taskDefinitionId);
    this.inputParameters$ = this.taskDefinition$.pipe(map((td) => td?.inputParameters));
    this.outputParameters$ = this.taskDefinition$.pipe(map((td) => td?.outputParameters));
  }

  ngOnChanges(): void {
    const components = WORKFLOW_ACTIONS[this.taskDefinitionId];

    if (!components?.panel) {
      console.error(
        `Component for task: ${this.taskDefinitionId} was not found, did you add it to the registration list?`,
      );
    }

    this.name = components.name;
    this.upgradeTitle = components.upgradeSubscriptionTitle || this.upgradeTitle;
    this.upgradeDescription = components.upgradeSubscriptionDescription || this.upgradeDescription;

    const viewContainerRef = this.taskNodeHost.viewContainerRef;
    viewContainerRef.clear();

    this.componentRef = viewContainerRef.createComponent(components.panel);
    this.componentRef.instance.step = this.step;
    this.componentRef.instance.title = this.title;
    this.componentRef.instance.automation = this.automation;
    this.componentRef.instance.precedingSteps = this.precedingSteps;
    this.componentRef.instance.branch = this.branch;
    this.componentRef.instance.viewTemplate = this.viewSelectorTemplate;
    this.componentRef.instance.taskDefinitionId = this.taskDefinitionId;
    this.componentRef.instance.isGoal = this.isGoal;
  }

  editingPreventedWarning(): Observable<string> {
    return this.automationService.getEditingPreventedWarning(this.automation);
  }

  save(): void {
    this.stepIdentifierControl.setValue(this.stepIdentifierControl.value.trim());
    this.validationService.updateControlValidity(this.form);
    if (this.form.invalid) {
      this.isProcessing = false;
      this.snackbarService.errorSnack(this.translateService.instant('AUTOMATIONS.COMMON.FORM_INVALID'));
      return;
    }
    let customOutputParameters: OutputParameter[] = [];
    if (this.supportsCustomOutputParameters) {
      customOutputParameters = this.customOutputParamsService.buildCustomOutputParametersFromReturnValues();
    }
    const name = this.stepIdentifierControl.value;
    const notes = this.stepNotesControl.value;
    this.componentRef.instance
      .save(name, customOutputParameters, notes, this.groupId)
      .pipe(
        catchError((err) => {
          return throwError(err);
        }),
      )
      .subscribe();
  }

  next(): void {
    this.componentRef.instance.next();
  }

  delete(): void {
    this.confirmationModal
      .openModal({
        type: 'warn',
        title: this.isGoal
          ? 'AUTOMATIONS.DELETE_STEP_MODAL.GOAL_TITLE'
          : this.translateService.instant('AUTOMATIONS.DELETE_STEP_MODAL.TITLE'),
        message: this.isGoal
          ? 'AUTOMATIONS.DELETE_STEP_MODAL.GOAL_DESCRIPTION'
          : this.translateService.instant('AUTOMATIONS.DELETE_STEP_MODAL.DESCRIPTION'),
        confirmButtonText: this.translateService.instant('COMMON.ACTION_LABELS.DELETE'),
        cancelButtonText: this.translateService.instant('COMMON.ACTION_LABELS.CANCEL'),
        width: 540,
      })
      .pipe(first())
      .subscribe((userDidAction) => {
        if (userDidAction === true) {
          return this.componentRef.instance.delete();
        }
      });
  }

  back(): void {
    this.componentRef.instance.back();
  }

  get showBackButton(): boolean {
    return this.componentRef.instance.showBackButton;
  }

  get savingEnabled(): boolean {
    return this.componentRef.instance.savingEnabled;
  }

  set savingEnabled(p: boolean) {
    this.componentRef.instance.savingEnabled = p;
  }

  get showNextButton(): boolean {
    return this.componentRef.instance.showNextButton;
  }

  get showSaveButton(): boolean {
    return this.componentRef.instance.showSaveButton;
  }

  get showDataPassing(): boolean {
    return this.componentRef.instance.showDataPassing;
  }

  get isProcessing(): boolean {
    return this.componentRef.instance.isProcessing;
  }

  set isProcessing(p: boolean) {
    this.componentRef.instance.isProcessing = p;
  }

  upgrade(): void {
    this.analyticsService.trackEvent('upgrade-' + this.taskDefinitionId, 'automations', 'click');
    this.router.navigateByUrl('/upgrade-subscription-tier?showUpgradeDialog=true');
  }
}
